import { Checkbox, Empty, Input, message, Popconfirm, Select, Table, Tag, Tooltip, Typography } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { ButtonItem, SelectItem, SwitchItem } from "../../../Components/Items";

const { Option } = Select;
const { Text } = Typography;

const COLOR_CAMBIO = "#fffbe6";
const COLOR_IMPLICITO = "#E6FBFD";

const ANCHO_USUARIO = 240;
const ANCHO_LIMITAR = 80;
const ANCHO_TODOS = 70;
const ANCHO_PROYECTO = 120;

const porNombre = (a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" });

// Con limitarProyectos apagado, el usuario ve todos los proyectos activos de la empresa
// (sin consignacion y no ocultos) con que tenga al menos uno asignado.
const esVisibleSinLimitar = (proyecto) =>
  proyecto.estado === 1 && !proyecto.consignacion && !proyecto.ocultoProyecto;

const copiarAccesos = (accesos) => {
  const copia = {};
  Object.keys(accesos).forEach((usuarioId) => {
    copia[usuarioId] = {
      proyectos: { ...accesos[usuarioId].proyectos },
      limitarProyectos: accesos[usuarioId].limitarProyectos,
    };
  });
  return copia;
};

class AccesoProyectos extends Component {
  state = {
    loading: false,
    guardando: false,
    empresas: [],
    empresaId: null,
    usuarios: [],
    original: {},
    actual: {},
    busqueda: "",
    mostrarDeshabilitados: false,
    mostrarInactivos: false,
  };

  componentDidMount() {
    this.consultar();
  }

  componentDidUpdate(prevProps) {
    // Al volver a la pestana se recarga para recoger lo editado desde el modal,
    // salvo que haya cambios sin guardar.
    if (this.props.activo && !prevProps.activo && this.armarCambios().length === 0) {
      this.consultar();
    }
  }

  async consultar() {
    this.setState({ loading: true });
    try {
      const [resProyectos, resUsuarios] = await Promise.all([
        axios.post(
          "proyectos/selectProyectosPEmpresa",
          { usuarioPrincipal: this.props.usuarioPrincipal },
          { headers: this.props.headersToken }
        ),
        axios.post(
          "usuarios/selectUsuarios",
          { usuarioPrincipal: this.props.usuarioPrincipal },
          { headers: this.props.headersToken }
        ),
      ]);

      const empresas = Array.isArray(resProyectos.data)
        ? resProyectos.data
            .map((empresa) => ({
              _id: String(empresa.empresaId),
              nombre: empresa.nombre,
              proyectos: [...(empresa.proyectos || [])]
                .map((proyecto) => ({ ...proyecto, _id: String(proyecto._id) }))
                .sort(porNombre),
            }))
            .sort(porNombre)
        : [];

      const datosUsuarios = Array.isArray(resUsuarios.data) ? resUsuarios.data : [];
      const usuarios = datosUsuarios
        .map((usuario) => ({
          _id: String(usuario._id),
          nombre: [usuario.nombre, usuario.apellidoPaterno, usuario.apellidoMaterno].filter(Boolean).join(" "),
          nombreUsuario: usuario.nombreUsuario || "",
          perfilNombre: usuario.perfil?.nombre || "",
          estado: usuario.estado,
        }))
        .sort(porNombre);

      const original = {};
      datosUsuarios.forEach((usuario) => {
        const proyectos = {};
        (usuario.proyectos || []).forEach((proyectoId) => {
          proyectos[String(proyectoId)] = true;
        });
        original[String(usuario._id)] = { proyectos, limitarProyectos: !!usuario.limitarProyectos };
      });

      this.setState((state) => ({
        loading: false,
        empresas,
        usuarios,
        original,
        actual: copiarAccesos(original),
        empresaId: empresas.some((empresa) => empresa._id === state.empresaId)
          ? state.empresaId
          : empresas[0]?._id || null,
      }));
    } catch (e) {
      this.setState({ loading: false });
      message.error("No se pudo consultar el acceso a proyectos");
    }
  }

  armarCambios() {
    const { original, actual } = this.state;
    return Object.keys(actual)
      .map((usuarioId) => {
        const antes = original[usuarioId];
        const ahora = actual[usuarioId];
        if (!antes) return null;
        const agregar = Object.keys(ahora.proyectos).filter(
          (proyectoId) => ahora.proyectos[proyectoId] && !antes.proyectos[proyectoId]
        );
        const quitar = Object.keys(antes.proyectos).filter(
          (proyectoId) => antes.proyectos[proyectoId] && !ahora.proyectos[proyectoId]
        );
        const cambioLimitar = ahora.limitarProyectos !== antes.limitarProyectos;
        if (agregar.length === 0 && quitar.length === 0 && !cambioLimitar) return null;
        return {
          usuarioId,
          agregar,
          quitar,
          ...(cambioLimitar ? { limitarProyectos: ahora.limitarProyectos } : {}),
        };
      })
      .filter(Boolean);
  }

  contarCeldasCambiadas(cambios) {
    return cambios.reduce(
      (total, cambio) =>
        total + cambio.agregar.length + cambio.quitar.length + ("limitarProyectos" in cambio ? 1 : 0),
      0
    );
  }

  setAcceso(usuarioIds, proyectoIds, valor) {
    this.setState(({ actual }) => {
      const nuevo = { ...actual };
      usuarioIds.forEach((usuarioId) => {
        if (!nuevo[usuarioId]) return;
        nuevo[usuarioId] = { ...nuevo[usuarioId], proyectos: { ...nuevo[usuarioId].proyectos } };
        proyectoIds.forEach((proyectoId) => {
          nuevo[usuarioId].proyectos[proyectoId] = valor;
        });
      });
      return { actual: nuevo };
    });
  }

  setLimitar(usuarioId, valor) {
    this.setState(({ actual }) => ({
      actual: { ...actual, [usuarioId]: { ...actual[usuarioId], limitarProyectos: valor } },
    }));
  }

  descartar() {
    this.setState(({ original }) => ({ actual: copiarAccesos(original) }));
  }

  guardar() {
    const cambios = this.armarCambios();
    if (cambios.length === 0) return;
    this.setState({ guardando: true });
    axios
      .post(
        "usuarios/editarProyectosUsuarios",
        { usuarioPrincipal: this.props.usuarioPrincipal, cambios },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ guardando: false });
        if (res.data?.success === 2) {
          message.success("Se guardó el acceso a proyectos");
          this.consultar();
          this.props.onGuardado?.();
        } else if (res.data?.error) {
          message.error(res.data.error);
        } else {
          message.error("Ha sucedido un error y no se pudo guardar el acceso a proyectos");
        }
      })
      .catch(() => {
        this.setState({ guardando: false });
        message.error("Ha sucedido un error y no se pudo guardar el acceso a proyectos");
      });
  }

  esPropio(usuario) {
    return usuario._id === String(this.props.usuarioId);
  }

  render() {
    const {
      loading, guardando, empresas, empresaId, usuarios, original, actual,
      busqueda, mostrarDeshabilitados, mostrarInactivos,
    } = this.state;

    const empresa = empresas.find((valueEmpresa) => valueEmpresa._id === empresaId);
    const proyectosEmpresa = empresa?.proyectos || [];
    const proyectos = proyectosEmpresa.filter(
      (proyecto) => mostrarInactivos || (proyecto.estado === 1 && !proyecto.ocultoProyecto)
    );
    const proyectoIds = proyectos.map((proyecto) => proyecto._id);

    const texto = busqueda.trim().toLowerCase();
    const filas = usuarios.filter(
      (usuario) =>
        actual[usuario._id] &&
        (mostrarDeshabilitados || usuario.estado === 1) &&
        (!texto ||
          usuario.nombre.toLowerCase().includes(texto) ||
          usuario.nombreUsuario.toLowerCase().includes(texto))
    );
    const editables = filas.filter((usuario) => !this.esPropio(usuario));
    const editableIds = editables.map((usuario) => usuario._id);

    const tieneAcceso = (usuarioId, proyectoId) => !!actual[usuarioId]?.proyectos[proyectoId];
    const cambio = (usuarioId, proyectoId) =>
      !!original[usuarioId]?.proyectos[proyectoId] !== tieneAcceso(usuarioId, proyectoId);
    // Pertenecer a la empresa no depende del estado del proyecto (ver selectEmpresas)
    const perteneceAEmpresa = (usuarioId) =>
      proyectosEmpresa.some((proyecto) => tieneAcceso(usuarioId, proyecto._id));
    const accesoImplicito = (usuarioId, proyecto) =>
      !tieneAcceso(usuarioId, proyecto._id) &&
      !actual[usuarioId]?.limitarProyectos &&
      esVisibleSinLimitar(proyecto) &&
      perteneceAEmpresa(usuarioId);

    const cambios = this.armarCambios();
    const celdasCambiadas = this.contarCeldasCambiadas(cambios);

    const columnas = [
      {
        title: "Usuario",
        key: "usuario",
        width: ANCHO_USUARIO,
        fixed: "left",
        render: (_, usuario) => (
          <Tooltip title={this.esPropio(usuario) ? "No puedes cambiar tus propios proyectos aquí" : undefined}>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontWeight: 500 }}>
                {usuario.nombre}
                {usuario.estado !== 1 && <Tag style={{ marginLeft: 6 }}>Deshabilitado</Tag>}
              </div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {[usuario.nombreUsuario, usuario.perfilNombre].filter(Boolean).join(" · ")}
              </Text>
            </div>
          </Tooltip>
        ),
      },
      {
        title: <Tooltip title="Si está activo, sólo ve los proyectos marcados. Aplica a todas las empresas.">Limitar</Tooltip>,
        key: "limitar",
        width: ANCHO_LIMITAR,
        fixed: "left",
        align: "center",
        onCell: (usuario) => ({
          style: actual[usuario._id]?.limitarProyectos !== original[usuario._id]?.limitarProyectos
            ? { background: COLOR_CAMBIO }
            : undefined,
        }),
        render: (_, usuario) => (
          <SwitchItem
            size="small"
            checked={!!actual[usuario._id]?.limitarProyectos}
            disabled={this.esPropio(usuario)}
            onChange={(valor) => this.setLimitar(usuario._id, valor)}
          />
        ),
      },
      {
        title: "Todos",
        key: "todos",
        width: ANCHO_TODOS,
        fixed: "left",
        align: "center",
        render: (_, usuario) => {
          const marcados = proyectoIds.filter((proyectoId) => tieneAcceso(usuario._id, proyectoId)).length;
          return (
            <Checkbox
              checked={proyectoIds.length > 0 && marcados === proyectoIds.length}
              indeterminate={marcados > 0 && marcados < proyectoIds.length}
              disabled={this.esPropio(usuario) || proyectoIds.length === 0}
              onChange={(e) => this.setAcceso([usuario._id], proyectoIds, e.target.checked)}
            />
          );
        },
      },
      ...proyectos.map((proyecto) => {
        const marcados = editableIds.filter((usuarioId) => tieneAcceso(usuarioId, proyecto._id)).length;
        const apagado = proyecto.estado !== 1 || proyecto.ocultoProyecto;
        return {
          title: (
            <div style={{ textAlign: "center" }}>
              <Checkbox
                checked={editableIds.length > 0 && marcados === editableIds.length}
                indeterminate={marcados > 0 && marcados < editableIds.length}
                disabled={editableIds.length === 0}
                onChange={(e) => this.setAcceso(editableIds, [proyecto._id], e.target.checked)}
              />
              <Tooltip title={proyecto.nombre}>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: apagado ? "#999" : undefined,
                  }}
                >
                  {proyecto.nombre}
                </div>
              </Tooltip>
              {proyecto.consignacion && <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>Consig.</Tag>}
              {proyecto.estado !== 1 && <Tag style={{ margin: 0, fontSize: 10 }}>Inactivo</Tag>}
              {proyecto.ocultoProyecto && <Tag style={{ margin: 0, fontSize: 10 }}>Oculto</Tag>}
            </div>
          ),
          key: proyecto._id,
          width: ANCHO_PROYECTO,
          align: "center",
          onCell: (usuario) => {
            let background;
            if (cambio(usuario._id, proyecto._id)) background = COLOR_CAMBIO;
            else if (accesoImplicito(usuario._id, proyecto)) background = COLOR_IMPLICITO;
            return { style: background ? { background } : undefined };
          },
          render: (_, usuario) => {
            const casilla = (
              <Checkbox
                checked={tieneAcceso(usuario._id, proyecto._id)}
                disabled={this.esPropio(usuario)}
                onChange={(e) => this.setAcceso([usuario._id], [proyecto._id], e.target.checked)}
              />
            );
            return accesoImplicito(usuario._id, proyecto)
              ? <Tooltip title="Lo ve porque no tiene limitado el acceso a proyectos">{casilla}</Tooltip>
              : casilla;
          },
        };
      }),
    ];

    const anchoTabla = ANCHO_USUARIO + ANCHO_LIMITAR + ANCHO_TODOS + proyectos.length * ANCHO_PROYECTO;

    return (
      <div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <SelectItem
            placeholder="Empresa"
            width={280}
            noFocusSiguiente
            value={empresaId || undefined}
            onChange={(valor) => this.setState({ empresaId: valor })}
          >
            {empresas.map((valueEmpresa) => (
              <Option key={valueEmpresa._id} value={valueEmpresa._id}>
                {valueEmpresa.nombre}
              </Option>
            ))}
          </SelectItem>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar usuario"
            style={{ width: 220 }}
            value={busqueda}
            onChange={(e) => this.setState({ busqueda: e.target.value })}
          />
          <span>
            <SwitchItem
              size="small"
              checked={mostrarDeshabilitados}
              onChange={(valor) => this.setState({ mostrarDeshabilitados: valor })}
            />{" "}
            Mostrar deshabilitados
          </span>
          <span>
            <SwitchItem
              size="small"
              checked={mostrarInactivos}
              onChange={(valor) => this.setState({ mostrarInactivos: valor })}
            />{" "}
            Mostrar proyectos inactivos y ocultos
          </span>
          <ButtonItem
            type="default"
            size="small"
            blanco
            icon={<ReloadOutlined />}
            disabled={loading || guardando}
            onClick={() => this.consultar()}
          >
            Recargar
          </ButtonItem>
        </div>

        {!loading && empresas.length === 0 ? (
          <Empty description="No hay empresas con proyectos" />
        ) : (
          <>
            <Table
              size="small"
              rowKey="_id"
              columns={columnas}
              dataSource={filas}
              loading={loading}
              pagination={false}
              // Con scroll.x y la tabla vacia antd 4 oscila con la scrollbar de la pagina
              scroll={filas.length > 0 ? { x: anchoTabla, y: "60vh" } : undefined}
              onRow={(usuario) => ({ style: usuario.estado !== 1 ? { opacity: 0.55 } : undefined })}
              locale={{
                emptyText: proyectos.length === 0 && empresa
                  ? "Esta empresa no tiene proyectos para mostrar"
                  : "No hay usuarios",
              }}
            />

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, marginTop: 10 }}>
              <span style={{ fontSize: 12, color: "#595959" }}>
                <span style={{ display: "inline-block", width: 12, height: 12, background: COLOR_CAMBIO, border: "1px solid #ffe58f", marginRight: 6, verticalAlign: "middle" }} />
                Cambio sin guardar
              </span>
              <span style={{ fontSize: 12, color: "#595959" }}>
                <span style={{ display: "inline-block", width: 12, height: 12, background: COLOR_IMPLICITO, border: "1px solid #00BFD6", marginRight: 6, verticalAlign: "middle" }} />
                Lo ve sin estar marcado (no tiene limitado el acceso)
              </span>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: celdasCambiadas > 0 ? 500 : undefined }}>
                  {celdasCambiadas > 0
                    ? `${celdasCambiadas} ${celdasCambiadas === 1 ? "cambio" : "cambios"} sin guardar (${cambios.length} ${cambios.length === 1 ? "usuario" : "usuarios"})`
                    : "Sin cambios"}
                </span>
                <ButtonItem
                  type="default"
                  blanco
                  disabled={celdasCambiadas === 0 || guardando}
                  onClick={() => this.descartar()}
                >
                  Descartar
                </ButtonItem>
                <Popconfirm
                  title={`Se cerrará la sesión de ${cambios.length} ${cambios.length === 1 ? "usuario" : "usuarios"} para aplicar los cambios. ¿Continuar?`}
                  okText="Sí"
                  cancelText="No"
                  disabled={celdasCambiadas === 0 || guardando}
                  onConfirm={() => this.guardar()}
                >
                  <ButtonItem type="primary" cyan loading={guardando} disabled={celdasCambiadas === 0}>
                    Guardar
                  </ButtonItem>
                </Popconfirm>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    usuarioPrincipal: state.user.usuarioPrincipal,
    usuarioId: state.user.usuarioId,
  };
};

export default connect(mapStateToProps)(AccesoProyectos);
